import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { LayoutDashboard, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useViewer } from '@/hooks/use-viewer'
import { Logo } from '@/components/generals/logo'
import { buttonVariants } from '@/components/ui/button'

interface SiteHeaderProps {
    tone?: 'light' | 'dark'
}

const NAV_LINKS = [
    { href: '/events', label: 'Browse events' },
    { href: '/sell', label: 'For organizers' },
    { href: '/sell#pricing', label: 'Pricing' },
    { href: '/tickets', label: 'Find my tickets' }
]

export const SiteHeader: FC<SiteHeaderProps> = ({ tone = 'light' }): ReactNode => {
    const router = useRouter()
    const { viewer } = useViewer()
    const [open, setOpen] = useState(false)
    const dark = tone === 'dark'

    useEffect(() => {
        const close = () => setOpen(false)
        router.events.on('routeChangeStart', close)
        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const isActive = (href: string): boolean => {
        if (href.includes('#')) return false
        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    return (
        <header
            className={cn(
                'sticky top-0 z-40 border-b backdrop-blur-md',
                dark ? 'border-white/10 bg-ink/85 text-ink-foreground' : 'border-border/70 bg-background/85 text-foreground'
            )}
        >
            <div className='mx-auto flex h-16 max-w-7xl items-center gap-8 px-4 sm:px-6 lg:px-8'>
                <Logo tone={dark ? 'dark' : 'light'} />

                <nav aria-label='Main' className='hidden items-center gap-1 lg:flex'>
                    {NAV_LINKS.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className={cn(
                                'rounded-full px-3.5 py-2 text-sm font-medium transition-colors',
                                dark ? 'text-ink-muted hover:bg-white/10 hover:text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                isActive(link.href) && (dark ? 'bg-white/10 text-white' : 'bg-muted text-foreground')
                            )}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='ml-auto flex items-center gap-2'>
                    {viewer
                        ? (
                            <Link href='/dashboard' className={buttonVariants({ variant: dark ? 'accent' : 'primary', size: 'sm', className: 'hidden sm:inline-flex' })}>
                                <LayoutDashboard className='size-4' aria-hidden='true' />
                                Dashboard
                            </Link>
                        )
                        : (
                            <>
                                <Link
                                    href='/login'
                                    className={buttonVariants({ variant: dark ? 'glass' : 'ghost', size: 'sm', className: 'hidden sm:inline-flex' })}
                                >
                                    Log in
                                </Link>

                                <Link href='/signup' className={buttonVariants({ variant: dark ? 'accent' : 'primary', size: 'sm' })}>
                                    Start selling
                                </Link>
                            </>
                        )}

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        className={cn(
                            'inline-flex size-10 items-center justify-center rounded-full transition-colors lg:hidden',
                            dark ? 'hover:bg-white/10' : 'hover:bg-muted'
                        )}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </div>

            {open && (
                <nav
                    id='mobile-menu'
                    aria-label='Mobile'
                    className={cn('border-t px-4 pb-5 pt-3 lg:hidden', dark ? 'border-white/10' : 'border-border')}
                >
                    <ul className='flex flex-col gap-1'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className={cn(
                                        'block rounded-xl px-3 py-3 text-base font-medium',
                                        dark ? 'hover:bg-white/10' : 'hover:bg-muted'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}

                        <li className='mt-2'>
                            <Link
                                href={viewer ? '/dashboard' : '/login'}
                                className={buttonVariants({ variant: dark ? 'glass' : 'outline', className: 'w-full' })}
                            >
                                {viewer ? 'Go to dashboard' : 'Log in'}
                            </Link>
                        </li>
                    </ul>
                </nav>
            )}
        </header>
    )
}
